/**
 * Source pricing: the quote list, and a comparison for one SKU.
 *
 * The comparison ranks on the landed estimate rather than the headline unit price, because a
 * cheap quote with expensive freight is not a saving. A quote more than sixty days old is
 * badged stale rather than quietly trusted.
 */
import { useState } from 'react';
import { Card, EmptyState, MoneyText, Pill, Skeleton } from '@/components';
import { useSession } from '@/app/session';
import { formatPercent } from '@/lib/format';
import { useCompare, useStockList, useSupplierPrices } from './queries';
import { EMPTY_FILTERS } from './types';
import './stock.css';

export function PricingPage() {
  const { can } = useSession();
  const [productId, setProductId] = useState<number | null>(null);
  const prices = useSupplierPrices(null, null);
  const stock = useStockList(EMPTY_FILTERS);
  const compare = useCompare(productId ?? 0);

  const rows = prices.data?.items ?? [];

  // Every column on this page is money. A role without supplier_prices.read (the packer)
  // is not offered the tab, and a typed URL lands here rather than on an empty table.
  if (!can('supplier_prices.read')) {
    return (
      <div className="stock-page">
        <EmptyState message="Source pricing is not part of this role." />
      </div>
    );
  }

  return (
    <div className="stock-page">
      <Card title="Compare suppliers for one SKU" basis="manual">
        <label className="o-field">
          <span className="o-field__label">SKU</span>
          <select
            className="o-input"
            value={productId === null ? '' : String(productId)}
            onChange={(event) =>
              setProductId(event.target.value === '' ? null : Number(event.target.value))
            }
          >
            <option value="">Choose a SKU</option>
            {(stock.data?.items ?? []).map((row) => (
              <option key={row.product_id} value={row.product_id}>
                {row.sku ?? row.name}
              </option>
            ))}
          </select>
        </label>

        {productId === null ? (
          <p className="o-muted">Pick a SKU to see every quote against the current WAC.</p>
        ) : compare.isLoading ? (
          <Skeleton shape="row" lines={3} />
        ) : (compare.data?.rows.length ?? 0) === 0 ? (
          <EmptyState message="No quotes recorded for this SKU." />
        ) : (
          <>
            <p>
              Current WAC{' '}
              {compare.data?.wac_cents === null || compare.data?.wac_cents === undefined ? (
                <span className="o-muted">not known</span>
              ) : (
                <MoneyText cents={compare.data.wac_cents} showCents />
              )}
              {compare.data?.star ? (
                <>
                  {' '}
                  <Pill tone="ok">a source beats it by 5 percent or more</Pill>
                </>
              ) : null}
            </p>
            <table className="stock-table">
              <caption className="o-visually-hidden">Supplier comparison</caption>
              <thead>
                <tr>
                  <th scope="col">Supplier</th>
                  <th scope="col" className="stock-num">Unit price</th>
                  <th scope="col" className="stock-num">Landed estimate</th>
                  <th scope="col" className="stock-num">vs WAC</th>
                  <th scope="col">Lead p80</th>
                  <th scope="col" className="stock-num">MOQ</th>
                </tr>
              </thead>
              <tbody>
                {(compare.data?.rows ?? []).map((row) => (
                  <tr key={row.supplier_id}>
                    <td>
                      {row.supplier}
                      {row.stale ? (
                        <>
                          {' '}
                          <Pill tone="warn">stale</Pill>
                        </>
                      ) : null}
                    </td>
                    <td className="stock-num">
                      <MoneyText cents={row.unit_price_cents} showCents />
                    </td>
                    <td className="stock-num">
                      <MoneyText cents={row.landed_estimate_cents} showCents />
                    </td>
                    <td className="stock-num">
                      {row.vs_wac_pct === null ? (
                        <span className="o-muted">n/a</span>
                      ) : (
                        <span className={row.vs_wac_pct < 0 ? 'stock-better' : ''}>
                          {formatPercent(row.vs_wac_pct)}
                        </span>
                      )}
                    </td>
                    <td className="o-mono">
                      {row.lead_p80_days === null ? 'n/a' : `${Math.round(row.lead_p80_days)}d`}
                    </td>
                    <td className="stock-num">{row.moq ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Card>

      <Card title="All quotes" basis="manual">
        {prices.isLoading ? (
          <Skeleton shape="row" lines={6} />
        ) : rows.length === 0 ? (
          <EmptyState message="No supplier quotes recorded." />
        ) : (
          <div className="stock-scroll" data-allow-hscroll>
            <table className="stock-table">
              <caption className="o-visually-hidden">Supplier price list</caption>
              <thead>
                <tr>
                  <th scope="col">Supplier</th>
                  <th scope="col">Linked SKU</th>
                  <th scope="col">Description</th>
                  <th scope="col" className="stock-num">Box</th>
                  <th scope="col" className="stock-num">Per unit</th>
                  <th scope="col" className="stock-num">Negotiated</th>
                  <th scope="col" className="stock-num">MOQ</th>
                  <th scope="col">Quoted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.supplier_price_id}>
                    <td>{row.supplier_name}</td>
                    <td className="o-mono">{row.sku ?? row.vendor_sku ?? 'not linked'}</td>
                    <td>{row.description ?? ''}</td>
                    <td className="stock-num">
                      {row.box_price_cents === null ? (
                        <span className="o-muted">n/a</span>
                      ) : (
                        <>
                          <MoneyText cents={row.box_price_cents} showCents />
                          {row.units_per_box ? (
                            <span className="o-muted"> / {row.units_per_box}</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="stock-num">
                      <MoneyText cents={row.unit_price_cents} showCents />
                    </td>
                    <td className="stock-num">
                      {row.negotiated_price_cents === null ? (
                        <span className="o-muted">list</span>
                      ) : (
                        <MoneyText cents={row.negotiated_price_cents} showCents />
                      )}
                    </td>
                    <td className="stock-num">{row.moq ?? '-'}</td>
                    <td>
                      {row.valid_from ?? 'unknown'}
                      {row.stale ? (
                        <>
                          {' '}
                          <Pill tone="warn">stale</Pill>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
